import type React from "react"
import { createContext, useContext, useEffect } from "react"
import { useUserStore } from "../../store"
import { authClient } from "./authClient"
import { clearBearerToken } from "./desktopAuth"

interface User {
  id: string
  email: string
  name: string | null
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<boolean>
  register: (email: string, password: string, name?: string) => Promise<boolean>
  logout: () => void
  updateProfile: (data: { name?: string }) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setUser } = useUserStore((state) => state)
  const { data: session, isPending: loading } = authClient.useSession()

  // Keep Zustand store in sync with the better-auth session
  useEffect(() => {
    if (session?.user) {
      setUser({
        id: session.user.id,
        email: session.user.email,
        name: session.user.name ?? null,
      })
    } else if (!loading) {
      setUser(null)
    }
  }, [session, loading, setUser])

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await authClient.signIn.email({
        email,
        password,
        callbackURL: "/workout",
      })
      return !result.error
    } catch (error) {
      console.error("Login error:", error)
      return false
    }
  }

  const register = async (email: string, password: string, name?: string): Promise<boolean> => {
    try {
      const result = await authClient.signUp.email({
        email,
        password,
        name: name ?? "",
        callbackURL: "/workout",
      })
      return !result.error
    } catch (error) {
      console.error("Registration error:", error)
      return false
    }
  }

  const logout = async () => {
    await authClient.signOut()
    clearBearerToken()
    setUser(null)
  }

  const updateProfile = async (data: { name?: string }): Promise<void> => {
    try {
      await authClient.updateUser(data)
    } catch (error) {
      console.error("Profile update error:", error)
      throw error
    }
  }

  const user: User | null = session?.user
    ? {
        id: session.user.id,
        email: session.user.email,
        name: session.user.name ?? null,
      }
    : null

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  )
}
